/**
 * brandToast — drop-in replacement for the raw `toast` API that keeps
 * EventDrop's tonal grammar consistent across the Memory loop and the
 * organiser dashboard. Per product direction the voice stays:
 *
 *   calm · human · restrained
 *
 * Never:  technical · cold · playful · jokey · operational.
 *
 * Usage:
 *   import { brandToast } from '../lib/brandToast';
 *   brandToast.gentle('Polaroid saved to your photos ✦');
 *   brandToast.slipped('A memory slipped away · please try again ✦');
 *
 * The visual style matches the chapter modal — italic Georgia serif
 * on a dark glass surface with a hairline gold border. The ✦ glyph
 * is the platform's signature accent — used sparingly so it reads as
 * a signature, not a decoration.
 */
import { toast } from 'sonner';

const baseStyle = {
  background: 'rgba(20, 12, 6, 0.92)',
  backdropFilter: 'blur(14px)',
  border: '1px solid rgba(212,175,94,0.32)',
  borderRadius: 14,
  color: '#FFE7A8',
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontStyle: 'italic',
  fontSize: 14,
  letterSpacing: '-0.005em',
  lineHeight: 1.45,
  padding: '14px 18px',
  boxShadow: '0 18px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(212,175,94,0.10)',
};

const slippedStyle = {
  ...baseStyle,
  border: '1px solid rgba(255,159,142,0.42)',
  color: '#FFD7CC',
};

const ensureGlyph = (msg) => {
  const s = String(msg || '').trim();
  if (!s) return s;
  if (/[✦✧★◆♦]/.test(s)) return s;
  return `${s} ✦`;
};

export const brandToast = {
  // Success / acknowledgement — quiet confirmation that something
  // brand-aligned just happened.
  gentle: (message, opts = {}) =>
    toast(ensureGlyph(message), { duration: 3000, ...opts, style: { ...baseStyle, ...(opts.style || {}) } }),

  // Soft failure — never reads as a stack-trace. Reframes a technical
  // problem as a human pause.
  slipped: (message, opts = {}) =>
    toast(ensureGlyph(message), { duration: 4500, ...opts, style: { ...slippedStyle, ...(opts.style || {}) } }),

  // Informational — used when something neutral happens (e.g. "Link
  // copied · paste it anywhere"). Same visual as `gentle` but skips
  // the auto-glyph since most info lines already feel complete.
  whisper: (message, opts = {}) =>
    toast(String(message || ''), { duration: 3000, ...opts, style: { ...baseStyle, ...(opts.style || {}) } }),
};

export default brandToast;
